import React, {useMemo} from 'react';
import styles from './FundraiserDocumentsTable.module.scss';
import {TableColumnConfig, Text, Spin, Button, Icon, DropdownMenu} from '@gravity-ui/uikit';
import {FileText, TrashBin} from '@gravity-ui/icons';
import {DataTable, formatDateToLocalDate} from '../../shared';
import {FundraiserDocumentModel} from '../../entities';
import {useInfiniteScroll} from '../../shared/hooks/useInfiniteScroll';

interface FundraiserDocumentsTableProps {
  documents: FundraiserDocumentModel[];
  isLoading: boolean;
  fetchNextPage: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage: boolean;
  onDeleteDocument: (documentId: string) => void;
  onViewDocument: (document: FundraiserDocumentModel) => void;
}

export const FundraiserDocumentsTable = ({
  documents,
  isLoading,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  onDeleteDocument,
  onViewDocument,
}: FundraiserDocumentsTableProps) => {
  const SKELETON_ROWS = 5;

  const columns = useMemo<TableColumnConfig<FundraiserDocumentModel>[]>(
    () => [
      {
        id: 'title',
        name: 'Название документа',
        template: (item: FundraiserDocumentModel) => (
          <div className={styles.titleCell}>
            <Icon data={FileText} size={16} className={styles.fileIcon} />
            <Text variant="body-1">{item.title}</Text>
          </div>
        ),
      },

      {
        id: 'createdAt',
        name: 'Дата добавления',
        template: (item: FundraiserDocumentModel) => (
          <Text variant="body-1">{formatDateToLocalDate(item.createdAt)}</Text>
        ),
      },
      {
        id: 'more',
        name: () => <div />,
        template: (item: FundraiserDocumentModel) => (
          <div className={styles.moreCell} onClick={(e) => e.stopPropagation()}>
            <DropdownMenu
              items={[
                {
                  iconStart: <Icon size={16} data={TrashBin} />,
                  action: () => onDeleteDocument(item.id),
                  text: 'Удалить',
                  className: styles.removeDropdownItem,
                },
              ]}
            />
          </div>
        ),
      },
    ],
    [onDeleteDocument],
  );

  const {loadMoreRef} = useInfiniteScroll({
    hasNextPage: !!hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  return (
    <div className={styles.tableContainer}>
      <DataTable<FundraiserDocumentModel>
        onRowClick={(item) => onViewDocument(item)}
        isLoading={isLoading && !isFetchingNextPage}
        data={documents || []}
        columns={columns}
        edgePadding={false}
        skeletonRowCount={SKELETON_ROWS}
        emptyStateTitle="Документы не найдены"
        emptyStateDescription="Для данного сбора документы не найдены"
      />

      {isFetchingNextPage ? (
        <div className={styles.loadingSpinnerContainer}>
          <Spin size="m" />
        </div>
      ) : (
        hasNextPage && (
          <Button
            component={'div'}
            size="xl"
            width="max"
            view="normal"
            ref={loadMoreRef}
            className={styles.loadMoreTrigger}>
            <Text variant="body-2">Загрузить ещё</Text>
          </Button>
        )
      )}
    </div>
  );
};
